import Grid from "@mui/material/Grid";
import Group from "@/src/components/Wizard/Group";
import LegalEntityIcon from "@/src/assets/icons/contract/toggleable/IconCompany";
import PersonIcon from "@/src/assets/icons/provision/toggleable/IconPerson";
import { emailDefault, isPhoneNumberDefault, requiredValidator } from "@/src/services/validationRules";
import wizardStyle from "@/src/components/Form/wizardStyle";
import FormLegend from "@/src/components/Form/Common/FormLegend/FormLegend";
import ValidatorTextField from "@/src/components/FormFields/ValidatorTextField";
import Address from "@/src/components/Form/Common/Address/Address";
import PostOfficeBoxAddress from "@/src/components/Form/Common/Address/PostOfficeBoxAddress";
import Person from "@/src/components/Form/Common/Person/Person";
import React from "react";
import { useForm } from "@/src/provider/Form/FormProvider";
import { translate } from "@/src/services/translations/translations";
import { AbstractCase } from "@/src/types/Entities/AbstractCase";

type OpposingPartyTypeProps = {
  product: AbstractCase;
};

export default function OpposingPartyData({ product }: OpposingPartyTypeProps) {
  const { values } = useForm();

  return (
    <>
      <Grid size={12}>
        <Group
          type={"radio"}
          name={"opposingPartyType"}
          question={translate(`${product.name}.opposingPartyType.question`, values)}
          options={[
            {
              labelText: translate(`${product.name}.opposingPartyType.options.opposingPartyLegalEntity.label`, values),
              labelIcon: <LegalEntityIcon />,
              labelIconActive: <LegalEntityIcon active />,
              value: "opposingPartyLegalEntity",
            },
            {
              labelText: translate(`${product.name}.opposingPartyType.options.opposingPartyPerson.label`, values),
              labelIcon: <PersonIcon />,
              labelIconActive: <PersonIcon active />,
              value: "opposingPartyPerson",
            },
          ]}
          validators={[requiredValidator("Bitte wählen Sie eine der angezeigten Antworten aus")]}
        />
      </Grid>
      {values.opposingPartyType === "opposingPartyLegalEntity" && (
        <>
          <Grid
            container
            sx={{
              justifyContent: "flex-start",
              alignItems: "baseline"
            }}>
            <Grid sx={[wizardStyle.gridTopSpaced, wizardStyle.question]} size={12}>
              <FormLegend>{translate(`${product.name}.opposingPartyLegalEntity.organization.question`, values)}</FormLegend>
            </Grid>
            <Grid size={12}>
              <ValidatorTextField
                name={"opposingPartyLegalEntity.organization.legalName"}
                label={translate(`${product.name}.opposingPartyLegalEntity.organization.label`, values)}
                helperText={translate(`${product.name}.opposingPartyLegalEntity.organization.helperText`, values)}
                validators={[requiredValidator("Damit wir wissen, gegen wen wir rechtlich vorgehen müssen, benötigen wir den Namen der Gegenseite.")]}
              />
            </Grid>
            <Grid sx={[wizardStyle.gridTopSpaced, wizardStyle.question]} size={12}>
              <FormLegend>
                Bitte geben Sie möglichst alle weiteren Daten an, die Ihnen vorliegen. Sollten Ihnen nicht alle Daten vorliegen, lassen Sie die Felder gerne
                einfach leer.
              </FormLegend>
            </Grid>
            <Address path={"opposingPartyLegalEntity.organization.address"} headline={""} fieldsAreRequired={false} />
            <PostOfficeBoxAddress path={"opposingPartyLegalEntity.organization.address"} headline={""} fieldsAreRequired={false} />
            <Grid size={12}>
              <ValidatorTextField
                name={"opposingPartyLegalEntity.organization.mainEmail.email"}
                label={translate(`${product.name}.opposingPartyLegalEntity.organization.mainEmail.label`, values)}
                autoComplete={"email"}
                validators={[emailDefault]}
              />
            </Grid>
            <Grid size={12}>
              <ValidatorTextField
                name={"opposingPartyLegalEntity.organization.telephone"}
                label={translate(`${product.name}.opposingPartyLegalEntity.organization.telephone.label`, values)}
                autoComplete={"tel"}
                validators={isPhoneNumberDefault}
              />
            </Grid>
          </Grid>
        </>
      )}
      {values.opposingPartyType === "opposingPartyPerson" && (
        <>
          <Grid
            container
            sx={{
              justifyContent: "flex-start",
              alignItems: "baseline"
            }}>
            <Person path={"opposingPartyPerson"} headline={translate(`${product.name}.opposingPartyPerson.question`, values)} />
            <Grid sx={[wizardStyle.gridTopSpaced, wizardStyle.question]} size={12}>
              <FormLegend>
                Bitte geben Sie möglichst alle weiteren Daten an, die Ihnen vorliegen. Sollten Ihnen nicht alle Daten vorliegen, lassen Sie die Felder gerne
                einfach leer.
              </FormLegend>
            </Grid>
            <Address path={"opposingPartyPerson.residenceAddress"} headline={""} fieldsAreRequired={false} />
            <Grid size={12}>
              <ValidatorTextField
                name={"opposingPartyPerson.email"}
                label={translate(`${product.name}.opposingPartyPerson.email.label`, values)}
                autoComplete={"email"}
                validators={[emailDefault]}
              />
            </Grid>
            <Grid size={12}>
              <ValidatorTextField
                name={"opposingPartyPerson.telephone"}
                label={translate(`${product.name}.opposingPartyPerson.telephone.label`, values)}
                autoComplete={"tel"}
                validators={isPhoneNumberDefault}
              />
            </Grid>
          </Grid>
        </>
      )}
    </>
  );
}
